import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { USER_QUERY, SAVE_USER, USER_SAVED, RESET_PASSWORD, USER_PASSWORD_RESET } from '../io';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { FakeText, Card } from '@totalsoft/rocket-ui';
import UserPanel from './UserPanel';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { userNameVar } from '../variables';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { userValidator } from '../validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { isNil } from 'lodash';

const initState = {
  inactive: false,
  useNewPerson: false
};

const UserContainer = ({ userId, onRedirect }) => {
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(initState);
  const [validation, validate] = useDirtyFieldValidation(userValidator);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const addToast = useToast();

  const isNew = useMemo(() => isNil(userId) || userId === 'new', [userId]);

  const { loading, refetch } = useQuery(USER_QUERY, {
    variables: { id: userId },
    skip: isNew,
    onCompleted: data => {
      resetLens(data.user);
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useEffect(() => {
    const name = get(lens.name) ?? '-';
    userNameVar(name);
  }, [lens]);

  useLanguageTracker({
    onChange: refetch
  });

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  const [saveUser] = useMutation(SAVE_USER, {
    onError: () => {
      setSaving(false);
    }
  });

  const [resetPassword] = useMutation(RESET_PASSWORD);

  const handleSave = useCallback(() => {
    const user = get(lens);
    if (!validate(user)) return;

    const userInput = {
      id: user.id,
      name: user.name,
      remark: user.remark,
      inactive: user.inactive,
      personId: user.personId,
      partnerId: user.partnerId,
      person: {
        firstName: user.personFirstName,
        lastName: user.personLastName,
        uniqueId: user.personUniqueId
      }
    };

    setSaving(true);
    saveUser({ variables: { input: userInput } });
  }, [lens, validate, saveUser]);

  const handleResetPassword = useCallback(() => {
    const id = get(lens.id);
    resetPassword({ variables: { id } });
  }, [lens.id, resetPassword]);

  useSubscription(USER_SAVED, {
    onData: ({
      data: {
        data: { userSaved }
      },
      client
    }) => {
      if (isNew) onRedirect(userSaved.user.id);
      resetLens(userSaved.user);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'userPagedList' });
      setSaving(false);
      addToast(t('Users.UserSaved'), 'success');
    }
  });

  useSubscription(USER_PASSWORD_RESET, {
    onError: error => addToast(error, 'error'),
    onData: () => addToast(t('Users.PasswordReset'), 'success')
  });

  return (
    <>
      {loading ? (
        <Card>
          <FakeText lines={7} />
        </Card>
      ) : (
        <UserPanel
          lens={lens}
          isDirty={isDirty}
          isNew={isNew}
          saving={saving}
          onSave={handleSave}
          onResetPassword={handleResetPassword}
          validation={validation}
        />
      )}
    </>
  );
};

UserContainer.propTypes = {
  userId: PropTypes.number,
  onRedirect: PropTypes.func
};

export default UserContainer;
